import { isEqual } from "es-toolkit";
import { atom } from "jotai";
import { atomFamily } from "jotai/utils";

import { Logger } from "@moment/logging";

import { $accessTokenFunc } from "../authenication";
import { env } from "../env";
import { isValidTemplateId } from "./validation";

const logger = new Logger("template-preview-image");

const TEMPLATE_WORKSPACE = "moment-templates";

/**
 * Atom family that fetches and caches template preview images with authentication.
 * Returns a data URL string or null if the image doesn't exist or fails to load.
 */
export const $templatePreviewImage = atomFamily((templateId: string | null) => {
	const $image = atom(async (get): Promise<string | null> => {
		if (!templateId || !env.docsApiUrl) {
			return null;
		}

		// Validate templateId to prevent path traversal attempts
		if (!isValidTemplateId(templateId)) {
			logger.warn("Invalid templateId rejected", { templateId });
			return null;
		}

		try {
			const { getAccessTokenSilently } = get($accessTokenFunc);
			const token = await getAccessTokenSilently?.();

			const headers = new Headers();
			if (token) {
				headers.set("Authorization", `Bearer ${token}`);
			}

			const url = `${env.docsApiUrl}/api/v2/${TEMPLATE_WORKSPACE}/${templateId}/raw/.moment/template-preview.svg?ref=main`;
			const response = await fetch(url, { headers });

			if (!response.ok) {
				// 404 means no preview image - not an error, just return null
				if (response.status === 404) {
					logger.debug("No preview image found for template", { templateId });
					return null;
				}
				throw new Error(`Failed to fetch preview: ${response.statusText}`);
			}

			const buffer = await response.arrayBuffer();
			return await arrayBufferToDataUrl(buffer, "image/svg+xml");
		} catch (err) {
			logger.debug("Failed to load template preview image", { templateId, err });
			return null;
		}
	});
	$image.debugLabel = `templatePreviewImage::${templateId}`;
	return $image;
}, isEqual);

/** Convert ArrayBuffer to data URL */
async function arrayBufferToDataUrl(buffer: ArrayBuffer, mimeType: string): Promise<string> {
	return new Promise((resolve, reject) => {
		const blob = new Blob([buffer], { type: mimeType });
		const reader = new FileReader();
		reader.onload = (e) => {
			if (e.target?.result) {
				resolve(e.target.result as string);
			} else {
				reject(new Error("Failed to convert to data URL"));
			}
		};
		reader.onerror = () => reject(new Error("FileReader error"));
		reader.readAsDataURL(blob);
	});
}
